/**
 * Currency Context Provider
 * 
 * Manages currency state throughout the application with localStorage persistence
 * and user preference synchronization. Follows the same pattern as LanguageContext.
 */
import { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { type CurrencyCode } from '@shared/pricing';
import { detectCurrency, isValidCurrency } from '@/lib/currency-detection';

// Local storage key
const CURRENCY_STORAGE_KEY = 'gratimy-currency-preference';

// Define the context type
type CurrencyContextType = {
  currentCurrency: CurrencyCode;
  changeCurrency: (currency: CurrencyCode) => void;
  isDetecting: boolean;
  detectionMethod?: 'user_preference' | 'country_detection' | 'browser_locale' | 'timezone' | 'default';
};

// Create the context with default values
const CurrencyContext = createContext<CurrencyContextType>({
  currentCurrency: 'USD',
  changeCurrency: () => {},
  isDetecting: false,
});

// Define the props for the provider component
interface CurrencyProviderProps {
  children: ReactNode;
  userCountry?: string; // Pass user's country for better detection
  userCurrencyPreference?: string; // Pass user's saved preference from server
}

// Utility functions for localStorage management
function getCurrencyFromStorage(): CurrencyCode | null {
  try {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (stored && isValidCurrency(stored)) {
      return stored as CurrencyCode;
    }
  } catch (error) {
    // localStorage read failed silently
  }
  return null;
}

function setCurrencyInStorage(currency: CurrencyCode): void {
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
    
    // Dispatch custom event for cross-component communication
    window.dispatchEvent(new CustomEvent('currency-changed', { 
      detail: currency 
    }));
  } catch (error) {
    // localStorage write failed silently
  }
}

function initializeCurrency(userCountry?: string, userServerPreference?: string): { currency: CurrencyCode; method: string } {
  // Priority order:
  // 1. User's server-side saved preference (highest priority)
  // 2. localStorage preference
  // 3. Auto-detection based on user country/browser
  
  // Check server preference first (highest priority)
  if (userServerPreference && isValidCurrency(userServerPreference)) {
    // Sync server preference to localStorage for consistency
    setCurrencyInStorage(userServerPreference as CurrencyCode);
    return { currency: userServerPreference as CurrencyCode, method: 'user_preference' };
  }
  
  // Check localStorage preference
  const storedCurrency = getCurrencyFromStorage();
  if (storedCurrency) {
    return { currency: storedCurrency, method: 'user_preference' };
  }
  
  // Auto-detect currency
  const detectedCurrency = detectCurrency(userCountry);
  
  // Determine detection method
  let method = 'default';
  if (userCountry) {
    method = 'country_detection';
  } else if (detectedCurrency !== 'USD') {
    method = 'browser_locale';
  }
  
  // Save detected currency to localStorage
  setCurrencyInStorage(detectedCurrency);
  
  return { currency: detectedCurrency, method };
}

// Provider component that wraps the app
export function CurrencyProvider({ children, userCountry, userCurrencyPreference }: CurrencyProviderProps) {
  const [currencyState, setCurrencyState] = useState<CurrencyCode>('USD');
  const [isDetecting, setIsDetecting] = useState(true);
  const [detectionMethod, setDetectionMethod] = useState<CurrencyContextType['detectionMethod']>('default');

  // Initialize currency on mount
  useEffect(() => {
    const initCurrency = async () => {
      setIsDetecting(true);
      
      try {
        const { currency, method } = initializeCurrency(userCountry, userCurrencyPreference);
        setCurrencyState(currency);
        setDetectionMethod(method as CurrencyContextType['detectionMethod']);
      } catch (error) {
        setCurrencyState('USD');
        setDetectionMethod('default');
      } finally {
        setIsDetecting(false);
      }
    };
    
    initCurrency();
  }, [userCountry, userCurrencyPreference]);

  // Handle currency changes
  const handleCurrencyChange = (currency: CurrencyCode) => {
    if (!isValidCurrency(currency)) {
      return;
    }
    
    setCurrencyInStorage(currency);
    setCurrencyState(currency);
    setDetectionMethod('user_preference');
  };

  // Listen for currency change events from other components/tabs
  useEffect(() => {
    const handleCurrencyEvent = (e: CustomEvent<CurrencyCode>) => {
      if (isValidCurrency(e.detail)) {
        setCurrencyState(e.detail);
      }
    };

    window.addEventListener('currency-changed', handleCurrencyEvent as EventListener);
    
    return () => {
      window.removeEventListener('currency-changed', handleCurrencyEvent as EventListener);
    };
  }, []);

  // Auto-switch currency when language changes
  useEffect(() => {
    const handleLanguageEvent = (e: CustomEvent<string>) => {
      const langToCurrency: Record<string, CurrencyCode> = {
        'da': 'DKK',
      };
      const mappedCurrency = langToCurrency[e.detail];
      if (mappedCurrency && mappedCurrency !== currencyState) {
        handleCurrencyChange(mappedCurrency);
      }
    };

    window.addEventListener('language-changed', handleLanguageEvent as EventListener);
    return () => {
      window.removeEventListener('language-changed', handleLanguageEvent as EventListener);
    };
  }, [currencyState]);

  // Server preference always wins when present — override any auto-detected/localStorage value
  useEffect(() => {
    if (userCurrencyPreference && isValidCurrency(userCurrencyPreference) && userCurrencyPreference !== currencyState) {
      setCurrencyInStorage(userCurrencyPreference as CurrencyCode);
      setCurrencyState(userCurrencyPreference as CurrencyCode);
      setDetectionMethod('user_preference');
    }
  }, [userCurrencyPreference]);

  // Provide context value
  const contextValue: CurrencyContextType = {
    currentCurrency: currencyState,
    changeCurrency: handleCurrencyChange,
    isDetecting,
    detectionMethod,
  };

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
}

// Custom hook to use the currency context
export function useCurrency() {
  const context = useContext(CurrencyContext);
  
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  
  return context;
}

// Export context for advanced usage
export { CurrencyContext };